import React, { useState, useContext, useEffect } from 'react'
import {
  Container,
  HeaderDiv,
  HeaderText,
  CloseDiv,
  CloseImg,
  ProgressBarDiv,
  ProgressBarCircle,
  ProgressBarLine,
  InputRowDiv,
  InputDiv,
  InputLabel,
  Input,
  ButtonsDiv,
  CancelButton,
  NextButtonDiv,
  NextButtonImage,
  NextButtonText,
  WarningText,
} from './style'
import StockContext from '../../../../contexts/StockContext'
import ModalContext from '../../../../contexts/ModalContext'
import UserContext from '../../../../contexts/UserContext'
import cross from '../../../../assets/addStockModal/cross.svg'
import arrowRight from '../../../../assets/addStockModal/pages/arrow-right.svg'
import {
  FieldsToHeaders,
  fieldsToHeaders,
  StockInterface,
} from '../../../../pages/ClientPanel/Stock/StockTable/utils'
import 'font-awesome/css/font-awesome.min.css'
import StockDropdown from '../StockDropdown'
import InfiniteSelect from '../../../InfiniteSelect'
import PantoneSelect, { PantoneValue } from '../../../PantoneSelect'
import ApiService from '../../../../services/ApiService'
import { Info } from 'lucide-react'

const FirstCustomAddPage: React.FC = () => {
  const { stock, setStock, resetContext } = useContext(StockContext)
  const { setModalActive, setModalSection, itIsUpdate, stockMode, isVariation } =
    useContext(ModalContext)
  const { user } = useContext(UserContext)
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({})
  const [warningMessage, setWarningMessage] = useState('')
  const [activeMenu, setActiveMenu] = useState(0)
  const [selectedColorObject, setSelectedColorObject] = useState<{ id: any; name: string; pantone: PantoneValue | null } | null>(null)
  const [pendingColorPantone, setPendingColorPantone] = useState<PantoneValue | null>(null)
  const [pantoneError, setPantoneError] = useState(false)

  const isFashion = stockMode === 'fashion'

  const requiredFields = isFashion ? ['material', 'color'] : ['material', 'format', 'family', 'density']
  const numberFields = ['density', 'runNumber']

  useEffect(() => {
    if (!isFashion) return

    const existingPantone = stock.pantoneCode
      ? { code: stock.pantoneCode, hex: stock.pantoneHex || '' }
      : null

    console.debug('[FIRSTPAGE][useEffect] stock load', {
      stock,
      stockColor: stock.color,
      stockItemColorId: stock.itemColorId,
      stockPantoneCode: stock.pantoneCode,
      stockPantoneHex: stock.pantoneHex,
      existingPantone,
      selectedColorObject,
      pendingColorPantone,
    })

    if (stock.color) {
      setSelectedColorObject({
        id: stock.itemColorId || stock.color,
        name: stock.color,
        pantone: existingPantone,
      })

      if (existingPantone) {
        setPendingColorPantone(existingPantone)
      }

      setStock((prev) => ({
        ...prev,
        color: stock.color,
        itemColorId: stock.itemColorId ?? prev.itemColorId,
        pantoneCode: stock.pantoneCode || prev.pantoneCode,
        pantoneHex: stock.pantoneHex || prev.pantoneHex,
      }))
    } else {
      setSelectedColorObject(null)
      setPendingColorPantone(null)
    }
  }, [isFashion, stock.color, stock.itemColorId, stock.pantoneCode, stock.pantoneHex])

  const getHeaderTitle = () => {
    if (isVariation) return isFashion ? 'Criar Variação de Tecido' : 'Criar Variação de Estoque'
    if (isFashion) return itIsUpdate ? 'Editar Tecido' : 'Adicionar Tecido'
    return itIsUpdate ? 'Editar Estoque' : 'Adicionar Estoque'
  }
  const headerTitle = getHeaderTitle()

  const fieldsArrayNormal: string[][] = [
    ['material', 'heatTreatment', 'format'],
    ['originCountry', 'finishing', 'supplier'],
    ['stockStreet', 'shelf', 'stockLocation'],
    ['density', 'receivedDate', 'heatNumber'],
    ['family', 'fabrication', 'description'],
    ['characteristic'],
  ]

  const isStringFieldNormal: boolean[][] = [
    [true, true, true],
    [true, true, true],
    [true, true, true],
    [false, false, true],
    [true, true, true],
    [true],
  ]

  const fieldsArrayFashion: string[][] = [
    ['material', 'format', 'originCountry'],
    ['finishing', 'supplier', 'stockStreet'],
    ['shelf', 'stockLocation', 'receivedDate'],
    ['heatNumber', 'family', 'fabrication'],
    ['description', 'characteristic', 'color'],
  ]

  const isStringFieldFashion: boolean[][] = [
    [true, true, true],
    [true, true, true],
    [true, true, false],
    [true, true, true],
    [false, false, true],
  ]

  const fieldsArray = isFashion ? fieldsArrayFashion : fieldsArrayNormal
  const isStringField = isFashion ? isStringFieldFashion : isStringFieldNormal

  const areRequiredFieldsFilled = () => {
    return requiredFields.every(
      (field) => !!stock[field as keyof StockInterface],
    )
  }

  useEffect(() => {
    const newErrors: { [key: string]: boolean } = {}
    requiredFields.forEach((field) => {
      if (!stock[field as keyof StockInterface]) {
        newErrors[field] = true
      }
    })
    setErrors(newErrors)
    setWarningMessage('')
  }, [stock, isFashion])

  const applyDateMask = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 8)
    if (digits.length <= 2) return digits
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (numberFields.includes(name) && isNaN(Number(value))) {
      setWarningMessage(name)
      return
    }
    setWarningMessage('')
    setErrors((prev) => ({
      ...prev,
      [name]: requiredFields.includes(name) && !value,
    }))
    const maskedValue = name === 'receivedDate' ? applyDateMask(value) : value
    setStock({ ...stock, [name]: maskedValue })
  }

  const fetchDensity = async (materialName: string) => {
    try {
      const response = await ApiService.get(`/backend/material/density/${materialName}`)
      const { density } = response.data
      setStock((prevStock) => ({
        ...prevStock,
        density: density.toString(),
      }))
    } catch (error) {
      console.error('Error fetching density:', error)
    }
  }

  const handleDropdownClick = (value: string, field: string) => {
    console.log('🔍 [FIRSTPAGE] Dropdown selecionado:', {
      field,
      oldValue: stock[field as keyof StockInterface],
      newValue: value
    });
    
    setStock({ ...stock, [field]: value })
    setErrors((prev) => ({
      ...prev,
      [field]: requiredFields.includes(field) && !value,
    }))

    if (field === 'color' && value) {
      setErrors((prev) => ({ ...prev, color: false }))
      setWarningMessage('')
    }

    setActiveMenu(0)

    if (!isFashion && field === 'material' && value) {
      fetchDensity(value)
    }
    
    console.log('✅ [FIRSTPAGE] Stock atualizado:', {
      field,
      newStockValue: value,
      totalFilledFields: Object.keys({...stock, [field]: value}).filter(key => stock[key as keyof StockInterface] || (key === field && value)).length
    });
  }

  const nextPage = () => {
    console.log('🚀 [FIRSTPAGE] Iniciando validação para próxima página');
    console.log('📋 [FIRSTPAGE] Estado atual do stock:', {
      material: stock.material,
      format: stock.format,
      family: stock.family,
      density: stock.density,
      totalFields: Object.keys(stock).length,
      filledFields: Object.keys(stock).filter(key => stock[key as keyof StockInterface]).length
    });
    
    const newErrors: { [key: string]: boolean } = {}
    requiredFields.forEach((field) => {
      if (!stock[field as keyof StockInterface]) {
        newErrors[field] = true
      }
    })
    if (!isFashion && isVariation && !stock.color) {
      newErrors['color'] = true
    }
    if (isFashion && stock.color && !stock.pantoneCode) {
      setPantoneError(true)
      newErrors['pantone'] = true
    }
    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) {
      return
    }

    setModalSection('secondCustomAdd')
  }

  return (
    <Container>
      <HeaderDiv>
        <HeaderText>{headerTitle}</HeaderText>
        <CloseDiv
          onClick={() => {
            setModalActive(false)
            resetContext()
          }}
        >
          <CloseImg src={cross} alt='Close tab' />
        </CloseDiv>
      </HeaderDiv>
      <ProgressBarDiv>
        <ProgressBarCircle className='active' />
        <ProgressBarLine />
        <ProgressBarCircle />
        <ProgressBarLine />
        <ProgressBarCircle />
      </ProgressBarDiv>
      {fieldsArray.map((inputRow, rowIndex) => (
        <InputRowDiv key={rowIndex}>
          {inputRow.map((stockField: string, colIndex) => (
            <InputDiv key={colIndex}>
              {/* Fashion mode: render Cor with InfiniteSelect + PantoneSelect inline */}
              {isFashion && stockField === 'color' ? (
                <>
                  <InputLabel>
                    Cor
                    <Info size={15} style={{ marginLeft: 5 }} color={!stock.color ? 'red' : 'gray'} />
                  </InputLabel>
                  <div style={{ width: '15.5vw', marginTop: '0.8vh' }}>
                  <InfiniteSelect
                    mode="crud"
                    value={selectedColorObject}
                    onSelect={option => {
                      console.debug('[FIRSTPAGE][InfiniteSelect] onSelect option:', option)
                      if (!option) {
                        setSelectedColorObject(null)
                        setPendingColorPantone(null)
                        setStock(prev => ({ ...prev, color: '', pantoneCode: '', pantoneHex: '' }))
                        setErrors(prev => ({ ...prev, color: requiredFields.includes('color') }))
                        return
                      }
                      const colorItem = { id: option.id, name: option.name, pantone: (option as any).pantone ?? null }
                      setSelectedColorObject(colorItem)
                      setStock(prev => ({
                        ...prev,
                        color: option.name,
                        itemColorId: option.id ?? prev.itemColorId,
                        pantoneCode: colorItem.pantone?.code || prev.pantoneCode,
                        pantoneHex: colorItem.pantone?.hex || prev.pantoneHex,
                      }))
                      setErrors(prev => ({ ...prev, color: false }))
                      if (colorItem.pantone?.code) {
                        setPendingColorPantone(colorItem.pantone)
                        setStock(prev => ({ ...prev, pantoneCode: colorItem.pantone!.code, pantoneHex: colorItem.pantone!.hex || '' }))
                      } else {
                        setPendingColorPantone(null)
                        setStock(prev => ({ ...prev, pantoneCode: '', pantoneHex: '' }))
                      }
                    }}
                    fetchFunction={async (limit, offset, search?) => {
                      const response = await ApiService.stockLookup.getColors()
                      const items = Array.isArray(response.data) ? response.data : []
                      const mapped = items.map((c: any) => {
                        if (typeof c === 'string') return { id: c, name: c, pantone: null }
                        return { id: c.id || c.name, name: c.name, pantone: c.pantone ?? null }
                      })
                      return search
                        ? mapped.filter((m: any) => m.name?.toLowerCase().includes(search.toLowerCase()))
                        : mapped
                    }}
                    onItemCreate={async (name: string) => {
                      const tempItem = { id: `tmp-${Date.now()}`, name: name.trim(), pantone: null }
                      return tempItem as any
                    }}
                    onItemDelete={async (colorId: any) => {
                      const isTemp = typeof colorId === 'string' && String(colorId).startsWith('tmp-')
                      if (!isTemp) {
                        try { await ApiService.stockLookup.deleteColor(colorId) } catch (e) { console.error(e) }
                      }
                    }}
                    placeholder="Buscar ou criar cor"
                    allowCreate={true}
                    allowDelete={true}
                    showCreateOnlyIfNotExists={true}
                    minCreateLength={2}
                  />
                  </div>
                  {selectedColorObject && !selectedColorObject.pantone?.code && (
                    <div style={{ marginTop: 8, padding: 10, backgroundColor: '#f0f7ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
                      <label style={{ fontSize: 11, color: pantoneError ? '#e53e3e' : '#374151', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                        Pantone para <strong>{selectedColorObject.name}</strong> <span style={{ color: '#e53e3e' }}>*</span>
                      </label>
                      <PantoneSelect
                        value={pendingColorPantone}
                        onSelect={(p) => {
                          console.debug('[FIRSTPAGE][PantoneSelect] onSelect:', p)
                          if (p) {
                            setSelectedColorObject(prev => prev ? { ...prev, pantone: p } : prev)
                            setPendingColorPantone(p)
                            setPantoneError(false)
                            setStock(prev => ({ ...prev, pantoneCode: p.code, pantoneHex: p.hex || '' }))
                          } else {
                            setPendingColorPantone(null)
                            setStock(prev => ({ ...prev, pantoneCode: '', pantoneHex: '' }))
                          }
                        }}
                        placeholder="Buscar Pantone (mín. 3 letras)"
                      />
                    </div>
                  )}
                  {selectedColorObject?.pantone?.code && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 12, color: '#555' }}>
                      {selectedColorObject.pantone.hex && (
                        <span style={{
                          display: 'inline-block', width: 12, height: 12, borderRadius: 3,
                          backgroundColor: selectedColorObject.pantone.hex.startsWith('#') ? selectedColorObject.pantone.hex : `#${selectedColorObject.pantone.hex}`,
                          border: '1px solid rgba(0,0,0,0.15)',
                        }} />
                      )}
                      <span style={{ fontSize: 11 }}>{selectedColorObject.pantone.name ? `${selectedColorObject.pantone.name} (${selectedColorObject.pantone.code})` : selectedColorObject.pantone.code}</span>
                    </div>
                  )}
                  {errors['color'] && <WarningText>O campo Cor é obrigatório!</WarningText>}
                  {pantoneError && <WarningText>Selecione um Pantone para a cor!</WarningText>}
                </>
              ) : (
                <>
                  {activeMenu == rowIndex * 3 + colIndex + 1 &&
                  isStringField[rowIndex][colIndex] ? (
                    <StockDropdown
                      text={stock[stockField as keyof StockInterface]}
                      field={stockField}
                      openUpward={stockField === 'color'}
                      onChange={(value) => {
                        handleDropdownClick(value, stockField)
                      }}
                    />
                  ) : null}

                  <InputLabel htmlFor={stockField}>
                    {fieldsToHeaders[stockField as keyof FieldsToHeaders] || stockField}
                    {requiredFields.includes(stockField) && (
                      <Info
                        size={15}
                        style={{ marginLeft: 5 }}
                        color={`${
                          !stock[stockField as keyof StockInterface]
                            ? 'red'
                            : 'gray'
                        }`}
                      />
                    )}
                    {!isFashion && isVariation && stockField === 'color' && !requiredFields.includes('color') && (
                      <Info
                        size={15}
                        style={{ marginLeft: 5 }}
                        color={!stock.color ? 'red' : 'gray'}
                      />
                    )}
                  </InputLabel>
                  <Input
                    placeholder={stockField === 'format' && !isFashion ? 'Selecione uma opção' : stockField === 'format' && isFashion ? 'Digite ou selecione' : stockField === 'receivedDate' ? 'DD/MM/AAAA' : 'Digite aqui'}
                    name={stockField}
                    value={stock[stockField as keyof StockInterface] || ''}
                    readOnly={stockField === 'format' && !isFashion}
                    onFocus={() => {
                      setErrors((prev) => ({ ...prev, [stockField]: false }))
                      setActiveMenu(rowIndex * 3 + colIndex + 1)
                      if (stockField === 'color') setWarningMessage('')
                    }}
                    onBlur={() => {
                      setErrors((prev) => ({
                        ...prev,
                        [stockField]:
                          requiredFields.includes(stockField) &&
                          !stock[stockField as keyof StockInterface],
                      }))
                      setActiveMenu(0)
                    }}
                    onChange={stockField === 'format' && !isFashion ? undefined : handleChange}
                    onClick={() => {
                      if (stockField === 'format') {
                        setActiveMenu(rowIndex * 3 + colIndex + 1)
                      }
                    }}
                    autoComplete='off'
                    style={{
                      cursor: stockField === 'format' && !isFashion ? 'pointer' : 'text',
                      border: errors[stockField] ? '1.5px solid red' : undefined,
                    }}
                  />
                  {warningMessage === stockField && stockField !== 'color' && (
                    <WarningText>O campo tem que ser um número!</WarningText>
                  )}
                  {stockField === 'color' && errors['color'] && (
                    <WarningText>
                      {isFashion ? 'O campo Cor é obrigatório!' : 'O campo Cor é obrigatório para criar uma variação!'}
                    </WarningText>
                  )}
                  {stockField === 'material' && errors['material'] && isFashion && (
                    <WarningText>O campo Material é obrigatório!</WarningText>
                  )}
                </>
              )}
            </InputDiv>
          ))}
        </InputRowDiv>
      ))}

      <ButtonsDiv>
        <CancelButton onClick={() => setModalSection('addStockMenu')}>
          Cancelar
        </CancelButton>
        <NextButtonDiv
          onClick={nextPage}
          style={{
            pointerEvents: areRequiredFieldsFilled() ? 'auto' : 'none',
            opacity: areRequiredFieldsFilled() ? 1 : 0.5,
          }}
        >
          <NextButtonImage src={arrowRight} alt='Right arrow icon' />
          <NextButtonText>Próximo</NextButtonText>
        </NextButtonDiv>
      </ButtonsDiv>
    </Container>
  )
}

export default FirstCustomAddPage

